<script setup lang="ts">
import type { ShopperProductsTypes } from 'commerce-sdk-isomorphic'
import { A11y } from 'swiper/modules'
import SwiperCore from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/vue'
import 'swiper/css'

type Props = {
  /** Constructor item id of the product that opened the slide-in. */
  seedItemId: string
}

type UpsellItem = {
  itemId: string
  productId: string
  card: {
    productId: string
    productName: string
    price?: number
    tieredPrice?: ProductTileProps['prices']
    image?: { url: string; alt: string }
    url?: string
    promotions?: Pick<ShopperProductsTypes.ProductPromotion, 'promotionId'>[]
  }
  /** Only set for Constructor results; the Product Promotion is not reported to Constructor. */
  recommendation?: { strategyId?: string; position: number }
}

const props = defineProps<Props>()

const i18n = useI18n()
const preferencesStore = usePreferencesStore()
const basketsStore = useBasketsStore()
const userStore = useUserStore()
const { getRecommendationRotator } = useConstructor()
const { trackRecommendationView, trackRecommendationClick } = useConstructorTracker()
const { dispatch, addToBagLoading } = useContentActions()

const recommender = preferencesStore.site?.addToBagRecommender?.fields
const podId = recommender?.recommenderID
const heading = computed(
  () => recommender?.recommenderHeading || i18n.t('product_details_page.add_to_bag_popup.recommendations_title'),
)

const productPromotion = ref<UpsellItem>()
const recommendations = ref<UpsellItem[]>([])
const resultId = ref<string>()
const resultCount = ref(0)

// Basket lines nest c_groupID under productCustomAttributes; without it a ring maps by size, not by group.
const basketItemIds = computed(
  () =>
    new Set(
      (basketsStore.basket?.productItems ?? []).flatMap((item) => [
        item.productId,
        mapConstructorItemId({ ...item, c_groupID: item.productCustomAttributes?.c_groupID }),
      ]),
    ),
)

// A ring added through the Quickview can be another colour, so another Constructor item than its card.
const addedItemIds = ref(new Set<string>())
let pendingItemId: string | undefined

const items = computed(() => {
  const candidates = [...(productPromotion.value ? [productPromotion.value] : []), ...recommendations.value]
  const seen = new Set<string>()
  return candidates.filter((item) => {
    if (
      seen.has(item.itemId) ||
      addedItemIds.value.has(item.itemId) ||
      basketItemIds.value.has(item.itemId) ||
      basketItemIds.value.has(item.productId)
    ) {
      return false
    }
    seen.add(item.itemId)
    return true
  })
})

const swiper = ref<SwiperCore>()
const activeIndex = ref(0)
const swiperOptions = { modules: [A11y], slidesPerView: 1, spaceBetween: 16 }

async function getProductPromotion(): Promise<UpsellItem | undefined> {
  const sku = preferencesStore.site?.productPromotion?.sku
  if (!sku) return
  const product = await useProducts().getProduct(sku)
  if (!product?.inventory?.orderable || !product.inventory.ats) return
  return {
    itemId: mapConstructorItemId(product),
    productId: product.id,
    card: {
      productId: product.id,
      productName: product.name ?? '',
      price: product.price,
      tieredPrice: product.tieredPrices,
      image: { url: findPrimaryAngle(product.images), alt: product.name ?? '' },
      url: product.slugUrl,
      promotions: product.productPromotions,
    },
  }
}

/** Constructor carries SFCC promotion ids, so they render through the same PromotionList as the cards above. */
function toSfccPromotions(
  promotions: ProductTileProps['promotions'],
): Pick<ShopperProductsTypes.ProductPromotion, 'promotionId'>[] {
  const hiddenFrom = userStore.isLoggedIn ? 'guest' : 'member'
  return [...(promotions ?? [])].flatMap((promotion) =>
    'customerAvailability' in promotion && promotion.customerAvailability !== hiddenFrom
      ? [{ promotionId: promotion.id }]
      : [],
  )
}

async function getRecommendations(): Promise<UpsellItem[]> {
  if (!podId) return []
  const rotator = await getRecommendationRotator(podId, [props.seedItemId])
  resultId.value = rotator?.resultId
  resultCount.value = rotator?.totalProducts ?? 0
  return (
    rotator?.products?.map((product, index) => ({
      itemId: product.constructorItemId || product.id,
      productId: product.id,
      card: {
        productId: product.id,
        productName: product.name ?? '',
        tieredPrice: product.prices,
        image: product.defaultImage
          ? { url: product.defaultImage.src, alt: product.defaultImage.alt ?? '' }
          : undefined,
        url: product.pdpUrl,
        promotions: toSfccPromotions(product.promotions),
      },
      recommendation: { strategyId: product.constructorStrategyId, position: index + 1 },
    })) ?? []
  )
}

// Views are sent per result as it scrolls into the single-card carousel, not for the whole pod at once.
const viewedItemIds = new Set<string>()
function trackView(item?: UpsellItem) {
  // The slide-in stays mounted once closed, so a pod that answers after the shopper has left isn't a view.
  if (!basketsStore.isShowAddToBagPopup) return
  if (!podId || !item?.recommendation || viewedItemIds.has(item.itemId)) return
  viewedItemIds.add(item.itemId)
  trackRecommendationView({
    url: window.location.href,
    podId,
    numResultsViewed: 1,
    items: [{ itemId: item.itemId, itemName: item.card.productName, variationId: item.productId }],
    resultCount: resultCount.value,
    resultId: resultId.value,
    seedItemIds: props.seedItemId,
  })
}

function trackClick(item: UpsellItem) {
  if (!podId || !item.recommendation?.strategyId) return
  trackRecommendationClick({
    itemId: item.itemId,
    itemName: item.card.productName,
    variationId: item.productId,
    podId,
    strategyId: item.recommendation.strategyId,
    resultId: resultId.value,
    resultCount: resultCount.value,
    resultPositionOnPage: item.recommendation.position,
    seedItemIds: props.seedItemId,
  })
}

async function addToBag(item: UpsellItem) {
  trackClick(item)
  pendingItemId = item.itemId
  await dispatch('addToBag', { productId: item.productId })
}

basketsStore.$onAction(({ name, args, after }) => {
  if (name !== 'addItem' || !pendingItemId || !basketsStore.isShowAddToBagPopup) return
  const [{ popup }] = args
  if (!popup || popup.isGiftWithPurchase) return
  const itemId = pendingItemId
  after(() => {
    if (basketsStore.error) return
    addedItemIds.value.add(itemId)
    pendingItemId = undefined
  })
})

function pushCarouselEvent(type: CarouselNavigationType, direction?: 'next' | 'prev') {
  pushEventToDataLayer(
    'custom.carousel.navigate',
    {
      group: heading.value,
      type,
      direction,
      info: {
        title: items.value[activeIndex.value]?.card.productName,
        position: `${activeIndex.value}`,
        count: items.value.length,
      },
    },
    i18n.locale.value,
  )
}

function onSlideChange(instance: SwiperCore) {
  activeIndex.value = instance.activeIndex
}

// Swiper also fires slideChange when it clamps the index after a card is added; only a shopper's
// slide runs the transition callbacks.
let navigationType: CarouselNavigationType = 'swipe'
function onShopperSlide(instance: SwiperCore) {
  pushCarouselEvent(navigationType, instance.activeIndex > instance.previousIndex ? 'next' : 'prev')
  navigationType = 'swipe'
}

function navigate(direction: 'next' | 'prev') {
  navigationType = 'arrow'
  if (direction === 'next') swiper.value?.slideNext()
  else swiper.value?.slidePrev()
}

// A Quickview add can drop cards either side of the one on show, so the next is found by identity, not index.
watch(items, async (next, previous) => {
  const following = previous.slice(activeIndex.value).find((item) => next.some(({ itemId }) => itemId === item.itemId))
  const index = following
    ? next.findIndex(({ itemId }) => itemId === following.itemId)
    : Math.min(activeIndex.value, next.length - 1)
  await nextTick()
  swiper.value?.update()
  if (index < 0) return
  swiper.value?.slideTo(index, undefined, false)
  activeIndex.value = index
})

watch([items, activeIndex], () => trackView(items.value[activeIndex.value]))

onMounted(async () => {
  const [promotion, results] = await Promise.all([
    getProductPromotion().catch((e) => console.error('AddToBagRecommendations/getProductPromotion', e)),
    getRecommendations(),
  ])
  productPromotion.value = promotion || undefined
  recommendations.value = results
})
</script>

<template>
  <!-- The results arrive after the slide-in opens; slide-fade is the slide-in's own transition. -->
  <Transition name="slide-fade">
    <section v-if="items.length" class="add-to-bag-recommendations" data-testid="add-to-bag-recommendations">
      <div class="add-to-bag-recommendations__header">
        <h3 class="add-to-bag-recommendations__title">{{ heading }}</h3>
        <div v-if="items.length > 1" class="add-to-bag-recommendations__arrows">
          <button
            :disabled="activeIndex === 0"
            :aria-label="$t('articleSwiper.button.previous')"
            data-testid="add-to-bag-recommendations__prev"
            @click="navigate('prev')"
          >
            <svg-icon name="mhj:arrow-carousel-left" />
          </button>
          <button
            :disabled="activeIndex >= items.length - 1"
            :aria-label="$t('articleSwiper.button.next')"
            data-testid="add-to-bag-recommendations__next"
            @click="navigate('next')"
          >
            <svg-icon name="mhj:arrow-carousel-right" />
          </button>
        </div>
      </div>
      <Swiper
        v-bind="swiperOptions"
        @swiper="(instance) => (swiper = instance)"
        @slideChange="onSlideChange"
        @slideChangeTransitionStart="onShopperSlide"
      >
        <SwiperSlide v-for="(item, index) in items" :key="item.itemId">
          <div
            class="add-to-bag-recommendations__item"
            data-testid="add-to-bag-recommendations__item"
            :data-test-item-id="item.itemId"
            :data-test-source="item.recommendation ? 'recommendation' : 'product-promotion'"
            :data-test-active="index === activeIndex"
            @click="trackClick(item)"
          >
            <ProductCardSummary :product="item.card" data-testid="add-to-bag-recommendations__item__card">
              <Button
                variant="text"
                class="add-to-bag-recommendations__add"
                data-testid="add-to-bag-recommendations__add"
                :disabled="addToBagLoading || basketsStore.loading"
                @click.stop="addToBag(item)"
              >
                <svg-icon name="mhj:bag-add" />
                <span>{{ $t('product_details_page.add_to_bag.button.text') }}</span>
              </Button>
            </ProductCardSummary>
          </div>
        </SwiperSlide>
      </Swiper>
    </section>
  </Transition>
</template>

<style lang="postcss">
.add-to-bag-recommendations {
  width: 100%;

  .add-to-bag-recommendations__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-bottom: var(--space-30);
    margin-bottom: var(--space-60);
    border-bottom: 1px solid var(--border-neutral-20);
  }

  .add-to-bag-recommendations__title {
    font-size: var(--text-large);
    text-transform: none;
    margin: 0;
  }

  .add-to-bag-recommendations__arrows {
    display: flex;

    button {
      display: flex;
      align-items: center;
      justify-content: center;
      width: var(--space-90);
      height: var(--space-90);
      padding: 0;
      cursor: pointer;

      &:hover:not(:disabled) {
        background-color: var(--surface-light-20);
      }

      &:disabled {
        cursor: default;
        opacity: 0.4;
      }
    }
  }

  .product-card--summary {
    padding-bottom: var(--space-50);

    .product-card__name {
      font-size: var(--text-base);
    }

    .product-card__pricing {
      font-size: var(--text-base);
      font-weight: normal;
    }
  }

  /* The Add to Bag button sits at the foot of the image, below however much the details take up. */
  .product-card--summary-wrapper {
    display: flex;
    flex-direction: column;
    padding: var(--space-30) 0 var(--space-30) var(--space-30);
  }

  .product-card--summary-details {
    padding-bottom: var(--space-30);
  }

  .add-to-bag-recommendations__add {
    display: flex;
    align-items: center;
    gap: var(--space-20);
    padding: var(--space-10) 0;
    align-self: flex-start;
    margin-top: auto;
    font-size: var(--text-small);
    text-decoration: none;

    svg.icons {
      height: var(--space-50);
    }
  }
}
</style>
